import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "vstack gap-3" }
const _hoisted_2 = {
  key: 0,
  class: "vstack gap-3",
  role: "region",
  "aria-labelledby": "error-summary-heading",
  "aria-live": "polite",
  "aria-atomic": "true"
}
const _hoisted_3 = {
  key: 0,
  id: "error-alert",
  class: "alert alert-danger fade show",
  role: "alert"
}
const _hoisted_4 = { class: "list-group" }
const _hoisted_5 = {
  key: 1,
  id: "warning-alert",
  class: "alert alert-warning fade show",
  role: "alert"
}
const _hoisted_6 = { class: "list-group" }
const _hoisted_7 = { class: "bg-light" }

import {ref, computed, watch, onMounted, nextTick} from "vue";
import {Death, DeathErrorWarning, DeathForm} from "./schema";
import {useDcraStore} from "@/stores/dcraStore";
import FormModal from "../Misc/FormModal.vue";
import SelectField from "@/components/Misc/SelectField.vue";
import {BInput, BTextarea, BAgency, BDatePicker} from "@/components/Fields";
import {array, date, number, object, string, ValidationError} from "yup";
import {useFormValidation} from "@/validation";


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateEditDeath',
  props: /*@__PURE__*/_mergeModels({
    id: {},
    startDate: {},
    currentState: {}
  }, {
    "modelValue": {type: Boolean},
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["dismiss", "submit", "update:modelValue"], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const modelValue = _useModel(__props, "modelValue");
const props = __props;
const emit = __emit;

var dcraStore = useDcraStore();

const {handleSubmit, errors, resetValidation} = useFormValidation();

const getDefaultForm = (): DeathForm => ({
    agency: "",
    lastName: "",
    firstName: "",
    middleName: "",
    sex: "",
    race: [],
    ethnicity: "",
    birthYear: "",
    dateOfDeath: undefined,
    locationName: "",
    streetAddress: "",
    city: "",
    state: props.currentState,
    zipCode: "",
    eventLocationType: "",
    mannerOfDeath: "",
    investigatingAgency: "",
    details: "",
    eventDescription: "",
});

const form = ref(getDefaultForm());

// Refs for scrolling to errors
const agencyRef = ref();
const lastNameRef = ref();
const firstNameRef = ref();
const middleNameRef = ref();
const sexRef = ref();
const raceRef = ref();
const ethnicityRef = ref();
const birthYearRef = ref();
const dateOfDeathRef = ref();
const eventLocationTypeRef = ref();
const locationNameRef = ref();
const streetAddressRef = ref();
const cityRef = ref();
const stateRef = ref();
const zipCodeRef = ref();
const mannerOfDeathRef = ref();
const investigatingAgencyRef = ref();
const detailsRef = ref();
const eventDescriptionRef = ref();

const fieldRefs = {
    agency: agencyRef,
    lastName: lastNameRef,
    firstName: firstNameRef,
    middleName: middleNameRef,
    sex: sexRef,
    race: raceRef,
    ethnicity: ethnicityRef,
    birthYear: birthYearRef,
    dateOfDeath: dateOfDeathRef,
    eventLocationType: eventLocationTypeRef,
    locationName: locationNameRef,
    streetAddress: streetAddressRef,
    city: cityRef,
    state: stateRef,
    zipCode: zipCodeRef,
    mannerOfDeath: mannerOfDeathRef,
    investigatingAgency: investigatingAgencyRef,
    details: detailsRef,
    eventDescription: eventDescriptionRef,
};

const schema = object<DeathForm>({
    agency: string().required("Agency is a required field"),
    lastName: string()
        .required("Last Name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    firstName: string()
        .required("First Name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    middleName: string().matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    sex: string().required("Sex is a required field"),
    race: array().required("Race is a required field").min(1, "Race is a required field"),
    ethnicity: string().required("Ethnicity is a required field"),
    birthYear: number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(new Date().getFullYear() - 123)
        .required("Birth Year is a required field"),
    dateOfDeath: date().required("Date and Time of Death is a required field"),
    locationName: string().matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    streetAddress: string()
        .required("Street Address is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    city: string()
        .required("City is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    state: string().required("State is required field").max(2),
    zipCode: string()
        .matches(/^\d{5}(-\d{4})?$/, "Zip Code must be in the format 12345 or 12345-6789")
        .required("Zip Code is a required field")
        .max(10),
    eventLocationType: string().required("Event Location Type is a required field"),
    mannerOfDeath: string().required("Manner of Death is a required field"),
    investigatingAgency: string().max(500,"Agency investigating must be less than 500 characters"),
    details: string()
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters")
        .max(500, "Details must be less than 500 characters"),
    eventDescription: string().required("Event Description is a required field").max(500,"Event Description must be less than 500 characters"),
});

var submitDisabled = ref(false);
var dcraErrors = ref([] as DeathErrorWarning[]);
const formLoadVersion = ref(0);
const errorLoadVersion = ref(0);
//Computed
const title = computed(() => (props.id > 0 ? "Edit Death" : "Add Death"));

//FUNctions
const submitForm = async () => {
    const isValid = await handleSubmit(schema, form.value);
    if (isValid) {
        if (props.id > 0) {
            await dcraStore.editDeath(props.id, form.value);
        } else {
            await dcraStore.createDeath(form.value);
        }

        window.location.href = "dataupload.aspx?source=8";
    } else {
        // Scroll to first error and focus for screen reader announcement
        await nextTick();
        const fieldOrder = ['agency', 'lastName', 'firstName', 'middleName', 'sex', 'race', 'ethnicity', 'birthYear', 'dateOfDeath', 'eventLocationType', 'locationName', 'streetAddress', 'city', 'state', 'zipCode', 'mannerOfDeath', 'investigatingAgency', 'details', 'eventDescription'];
        for (const field of fieldOrder) {
            if (errors.value[field]) {
                const el = fieldRefs[field]?.value;
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    // Focus first invalid input for screen reader to announce the error
                    const inputEl = el.querySelector('input, select, textarea, [role=\"listbox\"], .vue-select') as HTMLElement;
                    if (inputEl) {
                        inputEl.focus();
                    }
                    break;
                }
            }
        }
    }
};
var errorOnlyArray = computed(()=>{
    let errorArray = [] as DeathErrorWarning[];
    for (const error of dcraErrors.value){
        if(error.kind == "0"){
            errorArray.push(error);
        }
    }
    return errorArray;
})
var warningOnlyArray = computed(()=>{
    let warningArray = [] as DeathErrorWarning[];
    for (const error of dcraErrors.value){
        if(error.kind == "1"){
            warningArray.push(error);
        }
    }
    return warningArray;
})
const resetFormState = () => {
    formLoadVersion.value += 1;
    errorLoadVersion.value += 1;
    resetValidation();
    form.value = getDefaultForm();
    dcraErrors.value = [] as DeathErrorWarning[];
};

const setFormValues = async () => {
    const version = ++formLoadVersion.value;
    resetValidation();
    form.value = getDefaultForm();

    if (props.id > 0) {
        var response = await dcraStore.fetchDeath(props.id);

        if (version !== formLoadVersion.value) {
            return;
        }

        if (response.status == 200) {
            let deathForm = response.data;
            form.value = deathForm;
        }
    }
};

const getItemErrors = async () => {
    const version = ++errorLoadVersion.value;
    dcraErrors.value = [] as DeathErrorWarning[];

    if (props.id > 0) {
        let response = await dcraStore.getDeathErrors(props.id);

        if (version !== errorLoadVersion.value) {
            return;
        }

        if (response != null && response.status == 200) {
            dcraErrors.value = response.data ?? ([] as DeathErrorWarning[]);
        }
    }
};
//LifeCycle Hooks
onMounted(async () => {
    await dcraStore.fetchCodeLabelByKind();
    await setFormValues();
    await getItemErrors();
});

//Watches
watch(
    () => modelValue,
    async (isOpen) => {
        if (!isOpen) {
            resetFormState();
            return;
        }

        resetFormState();
        await setFormValues();
        await getItemErrors();
    },
    {immediate: true}
);

watch(
    () => props.id,
    async () => {
        if (!modelValue.value) {
            return;
        }

        await setFormValues();
        await getItemErrors();
    }
);

watch(
    () => form.value,
    async () => {
        try {
            if (errors.value.length == 0) {
                return;
            }

            errors.value = Array.isArray(errors.value) ? [] : {};
            await schema.validate(form.value, {abortEarly: false});
        } catch (err) {
            const error = err as ValidationError;
            if (error.name !== "ValidationError") throw err;

            // Build an object map of field -> message
            const newErrors: Record<string, string> = {};
            error.inner.forEach((it: ValidationError) => {
                if (it.path) newErrors[it.path] = it.message;
            });
            errors.value = newErrors;
        }
    },
    {deep: true}
);

__expose({ resetFormState });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", null, [
    _createVNode(FormModal, {
      title: title.value,
      modelValue: modelValue.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((modelValue).value = $event)),
      "submit-button": 'Submit',
      onSubmit: submitForm
    }, {
      default: _withCtx(() => [
        _createElementVNode("section", _hoisted_1, [
          (_unref(dcraErrors) && _unref(dcraErrors).length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (_unref(errorOnlyArray)?.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _cache[20] || (_cache[20] = _createElementVNode("h3", {
                        id: "error-summary-heading",
                        class: "alert-heading"
                      }, "Error(s):", -1)),
                      _createElementVNode("ul", _hoisted_4, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(errorOnlyArray), (error, index) => {
                          return (_openBlock(), _createElementBlock("li", {
                            class: "ms-3",
                            style: {"list-style-type":"disc"},
                            key: `error-${index}`
                          }, _toDisplayString(`${error.code} - ${error.message}`), 1))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (_unref(warningOnlyArray)?.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _cache[21] || (_cache[21] = _createElementVNode("h3", {
                        id: "warning-summary-heading",
                        class: "alert-heading"
                      }, "Warning(s):", -1)),
                      _createElementVNode("ul", _hoisted_6, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(warningOnlyArray), (warning, index) => {
                          return (_openBlock(), _createElementBlock("li", {
                            class: "ms-3",
                            style: {"list-style-type":"disc"},
                            key: `warning-${index}`
                          }, _toDisplayString(`${warning.code} - ${warning.message}`), 1))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "agencyRef",
            ref: agencyRef
          }, [
            _cache[22] || (_cache[22] = _createElementVNode("label", { for: "agency" }, [
              _createTextVNode("Agency"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BAgency), {
              id: "agency",
              name: "agency",
              modelValue: form.value.agency,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.agency) = $event)),
              "can-clear": false,
              error: _unref(errors).agency,
              "error-id": "agency-error",
              "select-first": true,
              disabled: props.id > 0,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).agency,
              "aria-describedby": _unref(errors).agency ? 'agency-error' : undefined
            }, null, 8, ["modelValue", "error", "disabled", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "lastNameRef",
            ref: lastNameRef
          }, [
            _cache[23] || (_cache[23] = _createElementVNode("label", { for: "lastName" }, [
              _createTextVNode("Last Name"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BInput), {
              id: "lastName",
              name: "lastName",
              type: "text",
              error: _unref(errors).lastName,
              "error-id": "lastName-error",
              required: "required",
              modelValue: form.value.lastName,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.lastName) = $event)),
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).lastName,
              "aria-describedby": _unref(errors).lastName ? 'lastName-error' : undefined
            }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "firstNameRef",
            ref: firstNameRef
          }, [
            _cache[24] || (_cache[24] = _createElementVNode("label", { for: "firstName" }, [
              _createTextVNode("First Name"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BInput), {
              id: "firstName",
              name: "firstName",
              type: "text",
              error: _unref(errors).firstName,
              "error-id": "firstName-error",
              required: "required",
              modelValue: form.value.firstName,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.firstName) = $event)),
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).firstName,
              "aria-describedby": _unref(errors).firstName ? 'firstName-error' : undefined
            }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "middleNameRef",
            ref: middleNameRef
          }, [
            _cache[25] || (_cache[25] = _createElementVNode("label", { for: "middleName" }, "Middle Name", -1)),
            _createVNode(_unref(BInput), {
              id: "middleName",
              name: "middleName",
              type: "text",
              error: _unref(errors).middleName,
              "error-id": "middleName-error",
              modelValue: form.value.middleName,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.middleName) = $event)),
              "aria-invalid": !!_unref(errors).middleName,
              "aria-describedby": _unref(errors).middleName ? 'middleName-error' : undefined
            }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "sexRef",
            ref: sexRef
          }, [
            _cache[26] || (_cache[26] = _createElementVNode("label", { for: "sex" }, [
              _createTextVNode("Sex"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(SelectField, {
              id: "sex",
              class: "w-100",
              modelValue: form.value.sex,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.sex) = $event)),
              "select-items": _unref(dcraStore).getSex,
              mode: "single",
              error: _unref(errors).sex,
              "error-id": "sex-error",
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).sex,
              "aria-describedby": _unref(errors).sex ? 'sex-error' : undefined
            }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "raceRef",
            ref: raceRef
          }, [
            _cache[27] || (_cache[27] = _createElementVNode("label", { for: "race" }, [
              _createTextVNode("Race"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(SelectField, {
              id: "race",
              class: "w-100",
              modelValue: form.value.race,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.race) = $event)),
              "select-items": _unref(dcraStore).getRace,
              mode: "tags",
              placeholder: "Select all that apply",
              error: _unref(errors).race,
              "error-id": "race-error",
              "include-show-all-option": false,
              "hide-selected": true,
              "close-on-select": true,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).race,
              "aria-describedby": _unref(errors).race ? 'race-error' : undefined
            }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "ethnicityRef",
            ref: ethnicityRef
          }, [
            _cache[28] || (_cache[28] = _createElementVNode("label", { for: "ethnicity" }, [
              _createTextVNode("Ethnicity"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(SelectField, {
              id: "ethnicity",
              class: "w-100",
              modelValue: form.value.ethnicity,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.ethnicity) = $event)),
              "select-items": _unref(dcraStore).getEthnicity,
              mode: "single",
              error: _unref(errors).ethnicity,
              "error-id": "ethnicity-error",
              min: 1,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).ethnicity,
              "aria-describedby": _unref(errors).ethnicity ? 'ethnicity-error' : undefined
            }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "birthYearRef",
            ref: birthYearRef
          }, [
            _cache[29] || (_cache[29] = _createElementVNode("label", { for: "birthYear" }, [
              _createTextVNode("Birth Year"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BInput), {
              id: "birthYear",
              name: "birthYear",
              type: "number",
              error: _unref(errors).birthYear,
              "error-id": "birthYear-error",
              required: "required",
              modelValue: form.value.birthYear,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.birthYear) = $event)),
              min: new Date().getFullYear() - 123,
              placeholder: 'YYYY or 9999 for unknown',
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).birthYear,
              "aria-describedby": _unref(errors).birthYear ? 'birthYear-error' : undefined
            }, null, 8, ["error", "modelValue", "min", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "dateOfDeathRef",
            ref: dateOfDeathRef
          }, [
            _cache[30] || (_cache[30] = _createElementVNode("label", { for: "dateOfDeath" }, [
              _createTextVNode("Date and Time of Death"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BDatePicker), {
              id: "dateOfDeath",
              modelValue: form.value.dateOfDeath,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.value.dateOfDeath) = $event)),
              error: _unref(errors).dateOfDeath,
              "error-id": "dateOfDeath-error",
              "month-picker": false,
              "auto-apply": false,
              "enable-time-picker": true,
              "enable-minutes": true,
              "enable-seconds": false,
              "max-date": new Date(),
              "min-date": new Date(props.startDate),
              "auto-position": true,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).dateOfDeath,
              "aria-describedby": _unref(errors).dateOfDeath ? 'dateOfDeath-error' : undefined
            }, null, 8, ["modelValue", "error", "max-date", "min-date", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("div", {
            ref_key: "eventLocationTypeRef",
            ref: eventLocationTypeRef
          }, [
            _cache[31] || (_cache[31] = _createElementVNode("label", { for: "eventLocationType" }, [
              _createTextVNode("Event Location Type"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(SelectField, {
              id: "eventLocationType",
              class: "w-100",
              modelValue: form.value.eventLocationType,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.value.eventLocationType) = $event)),
              "select-items": _unref(dcraStore).getEventLocationType,
              mode: "single",
              error: _unref(errors).eventLocationType,
              "error-id": "eventLocationType-error",
              min: 1,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).eventLocationType,
              "aria-describedby": _unref(errors).eventLocationType ? 'eventLocationType-error' : undefined
            }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
          ], 512),
          _createElementVNode("fieldset", _hoisted_7, [
            _cache[37] || (_cache[37] = _createElementVNode("legend", { class: "fs-5" }, "Location of Death", -1)),
            _createElementVNode("div", {
              class: "pb-2",
              ref_key: "locationNameRef",
              ref: locationNameRef
            }, [
              _cache[32] || (_cache[32] = _createElementVNode("label", { for: "locationName" }, "Location Name", -1)),
              _createVNode(_unref(BInput), {
                id: "locationName",
                name: "locationName",
                type: "text",
                error: _unref(errors).locationName,
                "error-id": "locationName-error",
                modelValue: form.value.locationName,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.value.locationName) = $event)),
                "aria-invalid": !!_unref(errors).locationName,
                "aria-describedby": _unref(errors).locationName ? 'locationName-error' : undefined
              }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
            ], 512),
            _createElementVNode("div", {
              class: "pb-2",
              ref_key: "streetAddressRef",
              ref: streetAddressRef
            }, [
              _cache[33] || (_cache[33] = _createElementVNode("label", { for: "streetAddress" }, [
                _createTextVNode("Street Address"),
                _createElementVNode("span", {
                  class: "text-danger",
                  "aria-label": "required"
                }, "*")
              ], -1)),
              _createVNode(_unref(BInput), {
                id: "streetAddress",
                name: "streetAddress",
                type: "text",
                error: _unref(errors).streetAddress,
                "error-id": "streetAddress-error",
                required: "required",
                modelValue: form.value.streetAddress,
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.value.streetAddress) = $event)),
                "aria-required": "true",
                "aria-invalid": !!_unref(errors).streetAddress,
                "aria-describedby": _unref(errors).streetAddress ? 'streetAddress-error' : undefined
              }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
            ], 512),
            _createElementVNode("div", {
              class: "pb-2",
              ref_key: "cityRef",
              ref: cityRef
            }, [
              _cache[34] || (_cache[34] = _createElementVNode("label", { for: "city" }, [
                _createTextVNode("City"),
                _createElementVNode("span", {
                  class: "text-danger",
                  "aria-label": "required"
                }, "*")
              ], -1)),
              _createVNode(_unref(BInput), {
                id: "city",
                name: "city",
                type: "text",
                error: _unref(errors).city,
                "error-id": "city-error",
                required: "required",
                modelValue: form.value.city,
                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.value.city) = $event)),
                "aria-required": "true",
                "aria-invalid": !!_unref(errors).city,
                "aria-describedby": _unref(errors).city ? 'city-error' : undefined
              }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
            ], 512),
            _createElementVNode("div", {
              class: "pb-2",
              ref_key: "stateRef",
              ref: stateRef
            }, [
              _cache[35] || (_cache[35] = _createElementVNode("label", { for: "state" }, [
                _createTextVNode("State"),
                _createElementVNode("span", {
                  class: "text-danger",
                  "aria-label": "required"
                }, "*")
              ], -1)),
              _createVNode(SelectField, {
                id: "state",
                class: "w-100",
                modelValue: form.value.state,
                "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((form.value.state) = $event)),
                "select-items": _unref(dcraStore).getState,
                mode: "single",
                error: _unref(errors).state,
                "error-id": "state-error",
                min: 1,
                "aria-required": "true",
                "aria-invalid": !!_unref(errors).state,
                "aria-describedby": _unref(errors).state ? 'state-error' : undefined
              }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
            ], 512),
            _createElementVNode("div", {
              class: "pb-2",
              ref_key: "zipCodeRef",
              ref: zipCodeRef
            }, [
              _cache[36] || (_cache[36] = _createElementVNode("label", { for: "zipCode" }, [
                _createTextVNode("Zip Code"),
                _createElementVNode("span", {
                  class: "text-danger",
                  "aria-label": "required"
                }, "*")
              ], -1)),
              _createVNode(_unref(BInput), {
                id: "zipCode",
                name: "zipCode",
                type: "text",
                error: _unref(errors).zipCode,
                "error-id": "zipCode-error",
                required: "required",
                modelValue: form.value.zipCode,
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((form.value.zipCode) = $event)),
                maxlength: 10,
                "aria-required": "true",
                "aria-invalid": !!_unref(errors).zipCode,
                "aria-describedby": _unref(errors).zipCode ? 'zipCode-error' : undefined
              }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
            ], 512)
          ]),
          _createElementVNode("div", {
            ref_key: "mannerOfDeathRef",
            ref: mannerOfDeathRef
          }, [
            _cache[38] || (_cache[38] = _createElementVNode("label", { for: "mannerOfDeath" }, [
              _createTextVNode("Manner of Death"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(SelectField, {
              id: "mannerOfDeath",
              class: "w-100",
              modelValue: form.value.mannerOfDeath,
              "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((form.value.mannerOfDeath) = $event)),
              "select-items": _unref(dcraStore).getMannerOfDeath,
              mode: "single",
              error: _unref(errors).mannerOfDeath,
              "error-id": "mannerOfDeath-error",
              min: 1,
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).mannerOfDeath,
              "aria-describedby": _unref(errors).mannerOfDeath ? 'mannerOfDeath-error' : undefined
            }, null, 8, ["modelValue", "select-items", "error", "aria-invalid", "aria-describedby"])
          ], 512),
          (form.value.mannerOfDeath === 'I')
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                ref_key: "investigatingAgencyRef",
                ref: investigatingAgencyRef
              }, [
                _cache[39] || (_cache[39] = _createElementVNode("label", { for: "investigatingAgency" }, "Agency Investigating Death and Expected Completion Date", -1)),
                _createVNode(_unref(BTextarea), {
                  id: "investigatingAgency",
                  name: "investigatingAgency",
                  error: _unref(errors).investigatingAgency,
                  "error-id": "investigatingAgency-error",
                  modelValue: form.value.investigatingAgency,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((form.value.investigatingAgency) = $event)),
                  "max-length": 500,
                  "aria-invalid": !!_unref(errors).investigatingAgency,
                  "aria-describedby": _unref(errors).investigatingAgency ? 'investigatingAgency-error' : undefined
                }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
              ], 512))
            : _createCommentVNode("", true),
          (form.value.mannerOfDeath === 'O')
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                ref_key: "detailsRef",
                ref: detailsRef
              }, [
                _cache[40] || (_cache[40] = _createElementVNode("label", { for: "details" }, "Details", -1)),
                _createVNode(_unref(BTextarea), {
                  id: "details",
                  name: "details",
                  error: _unref(errors).details,
                  "error-id": "details-error",
                  modelValue: form.value.details,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((form.value.details) = $event)),
                  "aria-invalid": !!_unref(errors).details,
                  "aria-describedby": _unref(errors).details ? 'details-error' : undefined
                }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
              ], 512))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            ref_key: "eventDescriptionRef",
            ref: eventDescriptionRef
          }, [
            _cache[41] || (_cache[41] = _createElementVNode("label", { for: "eventDescription" }, [
              _createTextVNode("Event Description"),
              _createElementVNode("span", {
                class: "text-danger",
                "aria-label": "required"
              }, "*")
            ], -1)),
            _createVNode(_unref(BTextarea), {
              id: "eventDescription",
              name: "eventDescription",
              required: "required",
              error: _unref(errors).eventDescription,
              "error-id": "eventDescription-error",
              modelValue: form.value.eventDescription,
              "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((form.value.eventDescription) = $event)),
              "aria-required": "true",
              "aria-invalid": !!_unref(errors).eventDescription,
              "aria-describedby": _unref(errors).eventDescription ? 'eventDescription-error' : undefined
            }, null, 8, ["error", "modelValue", "aria-invalid", "aria-describedby"])
          ], 512)
        ])
      ]),
      _: 1
    }, 8, ["title", "modelValue"])
  ]))
}
}

})